import { message } from "@/components/base/message";
import { resolveErrorMessage } from "@/lib/error-message";

const notifiedErrors = new WeakSet<object>();
const recentMessages = new Map<string, number>();
const RECENT_MESSAGE_TTL_MS = 2000;

interface NotifyBusinessErrorOptions {
	fallback?: string;
	duration?: number;
	dedupeKey?: string;
}

function normalizeIdentifier(value: unknown): string | undefined {
	if (typeof value !== "string" && typeof value !== "number") return undefined;
	const normalized = String(value).trim();
	return normalized || undefined;
}

function readHeader(headers: unknown, name: string): string | undefined {
	if (!headers || typeof headers !== "object") return undefined;
	const headerAccessor = headers as {
		get?: (key: string) => unknown;
		[key: string]: unknown;
	};
	const direct =
		normalizeIdentifier(headerAccessor[name]) ||
		normalizeIdentifier(headerAccessor[name.toLowerCase()]);
	if (direct) return direct;
	if (typeof headerAccessor.get === "function") {
		return (
			normalizeIdentifier(headerAccessor.get(name)) ||
			normalizeIdentifier(headerAccessor.get(name.toLowerCase()))
		);
	}
	return undefined;
}

export function extractErrorRequestId(error: unknown): string | undefined {
	if (!error || typeof error !== "object") return undefined;
	const candidate = error as {
		requestId?: unknown;
		request_id?: unknown;
		correlationId?: unknown;
		details?: {
			requestId?: unknown;
			request_id?: unknown;
			correlationId?: unknown;
			errorDetails?: { requestId?: unknown; request_id?: unknown };
			data?: { requestId?: unknown; request_id?: unknown; correlationId?: unknown };
		};
		response?: {
			headers?: unknown;
			data?: {
				requestId?: unknown;
				request_id?: unknown;
				correlationId?: unknown;
				error?: { requestId?: unknown; request_id?: unknown };
				data?: { requestId?: unknown; request_id?: unknown; correlationId?: unknown };
			};
		};
		headers?: unknown;
	};

	return (
		normalizeIdentifier(candidate.requestId) ||
		normalizeIdentifier(candidate.request_id) ||
		normalizeIdentifier(candidate.correlationId) ||
		normalizeIdentifier(candidate.details?.requestId) ||
		normalizeIdentifier(candidate.details?.request_id) ||
		normalizeIdentifier(candidate.details?.correlationId) ||
		normalizeIdentifier(candidate.details?.errorDetails?.requestId) ||
		normalizeIdentifier(candidate.details?.errorDetails?.request_id) ||
		normalizeIdentifier(candidate.details?.data?.requestId) ||
		normalizeIdentifier(candidate.details?.data?.request_id) ||
		normalizeIdentifier(candidate.details?.data?.correlationId) ||
		normalizeIdentifier(candidate.response?.data?.requestId) ||
		normalizeIdentifier(candidate.response?.data?.request_id) ||
		normalizeIdentifier(candidate.response?.data?.correlationId) ||
		normalizeIdentifier(candidate.response?.data?.error?.requestId) ||
		normalizeIdentifier(candidate.response?.data?.error?.request_id) ||
		normalizeIdentifier(candidate.response?.data?.data?.requestId) ||
		normalizeIdentifier(candidate.response?.data?.data?.request_id) ||
		normalizeIdentifier(candidate.response?.data?.data?.correlationId) ||
		readHeader(candidate.response?.headers, "x-request-id") ||
		readHeader(candidate.response?.headers, "x-correlation-id") ||
		readHeader(candidate.headers, "x-request-id") ||
		readHeader(candidate.headers, "x-correlation-id")
	);
}

function markNotified(error: unknown) {
	if (error && typeof error === "object") {
		notifiedErrors.add(error);
	}
}

function wasNotified(error: unknown): boolean {
	return !!error && typeof error === "object" && notifiedErrors.has(error);
}

function shouldSuppressRecentMessage(key: string): boolean {
	const now = Date.now();
	const lastShownAt = recentMessages.get(key);
	if (lastShownAt && now - lastShownAt < RECENT_MESSAGE_TTL_MS) {
		return true;
	}
	recentMessages.set(key, now);
	for (const [recentKey, shownAt] of recentMessages) {
		if (now - shownAt >= RECENT_MESSAGE_TTL_MS) {
			recentMessages.delete(recentKey);
		}
	}
	return false;
}

export function notifyBusinessError(error: unknown, options: NotifyBusinessErrorOptions = {}) {
	if (wasNotified(error)) return;
	const errorMessage = resolveErrorMessage(error, options.fallback || "操作失败，请稍后重试");
	const requestId = extractErrorRequestId(error);
	const visibleMessage = requestId ? `${errorMessage}（错误ID: ${requestId}）` : errorMessage;
	const dedupeKey = options.dedupeKey || visibleMessage;
	markNotified(error);
	if (shouldSuppressRecentMessage(dedupeKey)) return;
	message.error({ content: visibleMessage, duration: options.duration ?? 5 });
}
